import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { apiAdmin, setSuperAdminToken, getDisplayMessage } from '@/api/apiClient';
import { Plus, Pencil, GripVertical, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { MODULOS_LISTA } from '@/lib/modulos';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const formatMoney = (v) =>
  v != null ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v)) : '-';

export default function AdminPlanos() {
  const [planos, setPlanos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [modal, setModal] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [confirmDesativar, setConfirmDesativar] = useState(null);
  const [form, setForm] = useState({
    nome: '',
    descricao: '',
    preco_mensal: '',
    preco_anual: '',
    usuarios_inclusos: 5,
    editais_mensais: '',
    propostas_mensais: '',
    storage_gb: 1,
    ativo: true,
    modulos_habilitados: [],
  });

  const carregarPlanos = async () => {
    setLoading(true);
    setErro('');
    try {
      const res = await apiAdmin.get('/api/admin/planos');
      setPlanos(Array.isArray(res) ? res : []);
    } catch (err) {
      if (err?.status === 401) {
        setSuperAdminToken(null);
        window.location.href = '/admin';
        return;
      }
      setErro(getDisplayMessage(err, 'Erro ao carregar planos. Tente novamente.'));
      toast.error('Erro ao carregar planos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarPlanos();
  }, []);

  const abrirCriar = () => {
    setForm({
      nome: '',
      descricao: '',
      preco_mensal: '',
      preco_anual: '',
      usuarios_inclusos: 5,
      editais_mensais: '',
      propostas_mensais: '',
      storage_gb: 1,
      ativo: true,
      modulos_habilitados: [],
    });
    setModal({ tipo: 'criar' });
  };

  const abrirEditar = (plano) => {
    const modulos = Array.isArray(plano.modulosHabilitados) ? plano.modulosHabilitados : [];
    setForm({
      nome: plano.nome ?? '',
      descricao: plano.descricao ?? '',
      preco_mensal: plano.precoMensal != null ? String(plano.precoMensal) : '',
      preco_anual: plano.precoAnual != null ? String(plano.precoAnual) : '',
      usuarios_inclusos: plano.usuariosInclusos ?? 5,
      editais_mensais: plano.editaisMensais != null ? String(plano.editaisMensais) : '',
      propostas_mensais: plano.propostasMensais != null ? String(plano.propostasMensais) : '',
      storage_gb: plano.storageGb ?? 1,
      ativo: plano.ativo ?? true,
      modulos_habilitados: modulos,
    });
    setModal({ tipo: 'editar', plano });
  };

  const fecharModal = () => setModal(null);

  const salvar = async () => {
    if (!form.nome?.trim()) {
      toast.error('Informe o nome do plano.');
      return;
    }
    setSalvando(true);
    try {
      if (modal.tipo === 'criar') {
        await apiAdmin.post('/api/admin/planos', {
          nome: form.nome.trim(),
          descricao: form.descricao?.trim() || undefined,
          preco_mensal: Number(form.preco_mensal) || 0,
          preco_anual: form.preco_anual ? Number(form.preco_anual) : undefined,
          usuarios_inclusos: Number(form.usuarios_inclusos) || 5,
          editais_mensais: form.editais_mensais ? Number(form.editais_mensais) : undefined,
          propostas_mensais: form.propostas_mensais ? Number(form.propostas_mensais) : undefined,
          storage_gb: Number(form.storage_gb) || 1,
          ativo: !!form.ativo,
          modulos_habilitados: Array.isArray(form.modulos_habilitados) ? form.modulos_habilitados : [],
        });
        toast.success('Plano criado com sucesso.');
      } else {
        await apiAdmin.put('/api/admin/planos/' + modal.plano.id, {
          nome: form.nome.trim(),
          descricao: form.descricao?.trim() || undefined,
          preco_mensal: Number(form.preco_mensal) || 0,
          preco_anual: form.preco_anual ? Number(form.preco_anual) : undefined,
          usuarios_inclusos: Number(form.usuarios_inclusos) || 5,
          editais_mensais: form.editais_mensais ? Number(form.editais_mensais) : undefined,
          propostas_mensais: form.propostas_mensais ? Number(form.propostas_mensais) : undefined,
          storage_gb: Number(form.storage_gb) || 1,
          ativo: !!form.ativo,
          modulos_habilitados: Array.isArray(form.modulos_habilitados) ? form.modulos_habilitados : [],
        });
        toast.success('Plano atualizado com sucesso.');
      }
      fecharModal();
      carregarPlanos();
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao salvar plano. Tente novamente.'));
    } finally {
      setSalvando(false);
    }
  };

  const ativar = async (id) => {
    try {
      await apiAdmin.post('/api/admin/planos/' + id + '/ativar');
      carregarPlanos();
      toast.success('Plano ativado.');
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao ativar plano. Tente novamente.'));
    }
  };

  const desativar = async (id) => {
    try {
      await apiAdmin.post('/api/admin/planos/' + id + '/desativar');
      carregarPlanos();
      setConfirmDesativar(null);
      toast.success('Plano desativado.');
    } catch (err) {
      toast.error(getDisplayMessage(err, 'Erro ao desativar plano. Tente novamente.'));
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Planos</h1>
          <p className="text-slate-600">Gerencie os planos de assinatura</p>
        </div>
        <Button onClick={abrirCriar}>
          <Plus className="h-4 w-4" />
          Incluir plano
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          {erro && <p className="text-destructive text-sm mb-4">{erro}</p>}

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Preço mensal</TableHead>
                  <TableHead>Preço anual</TableHead>
                  <TableHead>Usuários</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {planos.map((plano) => (
                  <TableRow key={plano.id}>
                    <TableCell className="font-medium">{plano.nome}</TableCell>
                    <TableCell>{formatMoney(plano.precoMensal)}</TableCell>
                    <TableCell>{formatMoney(plano.precoAnual)}</TableCell>
                    <TableCell>{plano.usuariosInclusos}</TableCell>
                    <TableCell>
                      <Badge variant={plano.ativo ? 'default' : 'secondary'}>
                        {plano.ativo ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => abrirEditar(plano)} className="gap-1">
                          <Pencil className="h-4 w-4" />
                          Editar
                        </Button>
                        {plano.ativo ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-amber-600 border-amber-200 hover:bg-amber-50"
                            onClick={() => setConfirmDesativar(plano)}
                          >
                            Desativar
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-green-600 border-green-200 hover:bg-green-50"
                            onClick={() => ativar(plano.id)}
                          >
                            Ativar
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!modal} onOpenChange={(open) => !open && fecharModal()}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{modal?.tipo === 'criar' ? 'Novo plano' : 'Editar plano'}</DialogTitle>
            <DialogDescription>
              {modal?.tipo === 'criar' ? 'Crie um novo plano de assinatura.' : 'Atualize os dados do plano.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nome">Nome</Label>
              <Input
                id="nome"
                value={form.nome}
                onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
                placeholder="Ex: Profissional"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Input
                id="descricao"
                value={form.descricao}
                onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
                placeholder="Descrição do plano"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="preco_mensal">Preço mensal</Label>
                <Input
                  id="preco_mensal"
                  type="number"
                  step="0.01"
                  value={form.preco_mensal}
                  onChange={(e) => setForm((f) => ({ ...f, preco_mensal: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="preco_anual">Preço anual</Label>
                <Input
                  id="preco_anual"
                  type="number"
                  step="0.01"
                  value={form.preco_anual}
                  onChange={(e) => setForm((f) => ({ ...f, preco_anual: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="usuarios_inclusos">Usuários inclusos</Label>
                <Input
                  id="usuarios_inclusos"
                  type="number"
                  min="1"
                  value={form.usuarios_inclusos}
                  onChange={(e) => setForm((f) => ({ ...f, usuarios_inclusos: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="storage_gb">Storage (GB)</Label>
                <Input
                  id="storage_gb"
                  type="number"
                  min="1"
                  value={form.storage_gb}
                  onChange={(e) => setForm((f) => ({ ...f, storage_gb: e.target.value }))}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="editais_mensais">Editais/mês</Label>
                <Input
                  id="editais_mensais"
                  type="number"
                  value={form.editais_mensais}
                  onChange={(e) => setForm((f) => ({ ...f, editais_mensais: e.target.value }))}
                  placeholder="Ilimitado = vazio"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="propostas_mensais">Propostas/mês</Label>
                <Input
                  id="propostas_mensais"
                  type="number"
                  value={form.propostas_mensais}
                  onChange={(e) => setForm((f) => ({ ...f, propostas_mensais: e.target.value }))}
                  placeholder="Ilimitado = vazio"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Módulos habilitados no plano</Label>
              <p className="text-xs text-slate-500">Usuários deste plano só terão acesso aos módulos listados. A ordem abaixo define a ordem no menu &quot;Navegação&quot; — arraste para reordenar.</p>
              <div className="border rounded p-3 bg-slate-50 space-y-2">
                <DragDropContext
                  onDragEnd={(result) => {
                    if (!result.destination) return;
                    const arr = Array.isArray(form.modulos_habilitados) ? [...form.modulos_habilitados] : [];
                    const [removed] = arr.splice(result.source.index, 1);
                    arr.splice(result.destination.index, 0, removed);
                    setForm((f) => ({ ...f, modulos_habilitados: arr }));
                  }}
                >
                  <Droppable droppableId="modulos-habilitados">
                    {(provided) => (
                      <ul
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="space-y-1 min-h-[44px]"
                      >
                        {(Array.isArray(form.modulos_habilitados) ? form.modulos_habilitados : []).map((moduleId, index) => {
                          const m = MODULOS_LISTA.find((x) => x.id === moduleId);
                          const label = m?.label ?? moduleId;
                          return (
                            <Draggable key={moduleId} draggableId={moduleId} index={index}>
                              {(providedItem, snapshot) => (
                                <li
                                  ref={providedItem.innerRef}
                                  {...providedItem.draggableProps}
                                  {...providedItem.dragHandleProps}
                                  className={`flex items-center gap-2 px-3 py-2 rounded-md border bg-white text-sm font-medium ${snapshot.isDragging ? 'shadow-md border-blue-300' : 'border-slate-200'}`}
                                >
                                  <GripVertical className="h-4 w-4 text-slate-400 shrink-0" />
                                  <span className="flex-1">{label}</span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setForm((f) => ({
                                        ...f,
                                        modulos_habilitados: (f.modulos_habilitados || []).filter((id) => id !== moduleId),
                                      }));
                                    }}
                                    className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-red-600"
                                    aria-label={`Remover ${label}`}
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </li>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </ul>
                    )}
                  </Droppable>
                </DragDropContext>
                <div className="pt-2 border-t border-slate-200">
                  <p className="text-xs font-medium text-slate-600 mb-2">Adicionar módulo</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {MODULOS_LISTA.filter((m) => !(form.modulos_habilitados || []).includes(m.id)).map((m) => (
                      <div key={m.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`add-mod-${m.id}`}
                          onCheckedChange={() => {
                            setForm((f) => ({
                              ...f,
                              modulos_habilitados: [...(f.modulos_habilitados || []), m.id],
                            }));
                          }}
                        />
                        <Label htmlFor={`add-mod-${m.id}`} className="font-normal cursor-pointer text-sm">{m.label}</Label>
                      </div>
                    ))}
                    {MODULOS_LISTA.filter((m) => !(form.modulos_habilitados || []).includes(m.id)).length === 0 && (
                      <p className="text-xs text-slate-500 col-span-2">Todos os módulos já estão na lista.</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="ativo"
                checked={form.ativo}
                onCheckedChange={(checked) => setForm((f) => ({ ...f, ativo: !!checked }))}
              />
              <Label htmlFor="ativo" className="font-normal cursor-pointer">Plano ativo</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={fecharModal}>
              Cancelar
            </Button>
            <Button onClick={salvar} disabled={salvando || !form.nome?.trim()}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDesativar} onOpenChange={() => setConfirmDesativar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar desativação</AlertDialogTitle>
            <AlertDialogDescription>
              Deseja realmente desativar o plano {confirmDesativar?.nome}? O plano poderá ser reativado posteriormente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-amber-600 hover:bg-amber-700"
              onClick={() => confirmDesativar && desativar(confirmDesativar.id)}
            >
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
